import { HttpErrorResponse } from '@angular/common/http';
import type { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { RecaptchaService } from '@core/services/recaptcha/recaptcha.service';
import { RecaptchaChallengeService } from '@core/services/recaptcha/recaptcha-challenge.service';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { esChallengeRecaptcha } from './recaptcha.constants';

const HEADER_RECAPTCHA_TOKEN = 'X-ReCAPTCHA-Token';
const HEADER_RECAPTCHA_VERSION = 'X-ReCAPTCHA-Version';

const ENDPOINTS_PROTEGIDOS: { patron: string; accion: string }[] = [
  { patron: '/auditoria-trazabilidad/documentos-aceptacion', accion: 'aceptar_terminos' },
  { patron: '/otp/enviar', accion: 'enviar_otp' },
  { patron: '/otp/validar', accion: 'validar_otp' },
  { patron: '/biometria/sesion', accion: 'biometria_sesion' },
  { patron: '/biometria/validar', accion: 'validacion_biometria' },
  { patron: '/cliente/captura-datos-contacto', accion: 'captura_datos_contacto' },
  { patron: '/cliente/datos-prospecto', accion: 'obtener_datos_prospecto' },
  { patron: '/cliente/captura-datos-complementarios', accion: 'captura_datos_complementarios' },
  { patron: '/cliente/captura-datos-financieros-autorizaciones', accion: 'captura_datos_financieros' },
  { patron: '/cliente/validaciones/codigo-asesor', accion: 'validacion_codigo_asesor' },
  { patron: '/cliente/validaciones/sarlaft', accion: 'validacion_sarlaft' },
  { patron: '/validaciones/core-bantotal', accion: 'validacion_core_bantotal' },
  { patron: '/experian/validar', accion: 'validacion_experian' },
  { patron: '/simulaciones/cuenta-ahorro', accion: 'simulacion_cuenta_ahorro' },
  { patron: '/datos-maestros/', accion: 'consulta_datos_maestros' },
];

function crearSolicitudConTokenV2(req: HttpRequest<unknown>, tokenV2: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      [HEADER_RECAPTCHA_TOKEN]: tokenV2,
      [HEADER_RECAPTCHA_VERSION]: 'v2',
    },
  });
}

export const recaptchaInterceptor: HttpInterceptorFn = (req, next) => {
  const recaptchaService = inject(RecaptchaService);
  const challengeService = inject(RecaptchaChallengeService);

  if (req.headers.has(HEADER_RECAPTCHA_TOKEN)) {
    return next(req);
  }

  const endpointProtegido = ENDPOINTS_PROTEGIDOS.find((ep) => req.url.includes(ep.patron));

  if (!endpointProtegido) {
    return next(req);
  }

  return recaptchaService.getSilentToken(endpointProtegido.accion).pipe(
    switchMap((token: string) => {
      if (!token) {
        return next(req);
      }

      const solicitudConTokenV3 = req.clone({
        setHeaders: {
          [HEADER_RECAPTCHA_TOKEN]: token,
          [HEADER_RECAPTCHA_VERSION]: 'v3',
        },
      });

      return next(solicitudConTokenV3).pipe(
        catchError((error: HttpErrorResponse) => {
          if (!esChallengeRecaptcha(error)) {
            return throwError(() => error);
          }

          return challengeService
            .mostrarChallengeV2()
            .pipe(switchMap((tokenV2: string) => next(crearSolicitudConTokenV2(req, tokenV2))));
        }),
      );
    }),
  );
};
